import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Loader2, LogOut, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { PageHero } from "@/components/site/Blocks";
import {
  AVAILABILITY,
  LANGUAGE_LEVELS,
  PORTAL_PATHS,
  ROLE_SUGGESTIONS,
  SOCIAL_KEYS,
  maxBirthYear,
  MIN_BIRTH_YEAR,
  passwordSchema,
  profileSchema,
  registerSchema,
  routeForStatus,
  type ConsultantStatus,
  type PortalLocale,
} from "@/lib/consultant-portal";
import { PORTAL_STRINGS } from "@/lib/consultant-portal-i18n";
import {
  deleteOwnConsultantAccount,
  registerConsultant,
  saveConsultantProfile,
} from "@/lib/consultant-portal.functions";

const inputClass =
  "mt-2 w-full rounded-md border border-input bg-background px-4 py-3 text-sm outline-none focus:border-ring focus:ring-2 focus:ring-ring/30 read-only:opacity-70";
const btnPrimary =
  "inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-6 py-3 text-sm font-bold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-60";

function Shell({ title, intro, locale, children, wide }: { title: string; intro?: string; locale: PortalLocale; children: ReactNode; wide?: boolean }) {
  return (
    <>
      <PageHero eyebrow={PORTAL_STRINGS[locale].eyebrow} title={title} intro={intro ?? ""} />
      <section className="section-shell flex justify-center py-16">
        <div className={`w-full ${wide ? "max-w-3xl" : "max-w-md"} rounded-lg border border-border bg-card p-8 shadow-card`}>
          {children}
        </div>
      </section>
    </>
  );
}

function FieldError({ msg }: { msg?: string | undefined }) {
  return msg ? <p className="mt-1 text-xs font-medium text-destructive">{msg}</p> : null;
}

function issuesToErrors(issues: { path: PropertyKey[]; message: string }[], t: Record<string, string>) {
  const out: Record<string, string> = {};
  for (const i of issues) {
    const k = i.path.map(String).join(".");
    if (!out[k]) out[k] = t[i.message] ?? t["generic"]!;
  }
  return out;
}

/* ---------------- Register ---------------- */

export function RegisterPage({ locale }: { locale: PortalLocale }) {
  const t = PORTAL_STRINGS[locale];
  const p = PORTAL_PATHS[locale];
  const register = useServerFn(registerConsultant);
  const startedAt = useRef(Date.now());
  const [f, setF] = useState({ firstName: "", lastName: "", email: "", password: "", confirm: "", consent: false, website: "" });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const parsed = registerSchema.safeParse({
      firstName: f.firstName,
      lastName: f.lastName,
      email: f.email,
      password: f.password,
      consent: f.consent,
      locale,
      honeypot: f.website,
      startedAt: startedAt.current,
    });
    const errs = parsed.success ? {} : issuesToErrors(parsed.error.issues, t.errors);
    if (f.password !== f.confirm) errs["confirm"] = t.errors["password_mismatch"]!;
    setErrors(errs);
    if (!parsed.success || errs["confirm"]) return;
    setBusy(true);
    try {
      const res = await register({ data: parsed.data });
      if (res.ok) setDone(true);
      else toast.error(t.errors[res.error]);
    } catch {
      toast.error(t.errors["generic"]);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Shell title={t.registerTitle} intro={t.registerIntro} locale={locale}>
      {done ? (
        <p className="text-sm leading-relaxed text-muted-foreground" role="status">{t.checkEmail}</p>
      ) : (
        <form onSubmit={submit} noValidate>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block text-sm font-semibold">
              {t.firstName}
              <input className={inputClass} autoComplete="given-name" value={f.firstName} onChange={(e) => setF({ ...f, firstName: e.target.value })} />
              <FieldError msg={errors["firstName"]} />
            </label>
            <label className="block text-sm font-semibold">
              {t.lastName}
              <input className={inputClass} autoComplete="family-name" value={f.lastName} onChange={(e) => setF({ ...f, lastName: e.target.value })} />
              <FieldError msg={errors["lastName"]} />
            </label>
          </div>
          <label className="mt-4 block text-sm font-semibold">
            {t.email}
            <input type="email" className={inputClass} autoComplete="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
            <FieldError msg={errors["email"]} />
          </label>
          <label className="mt-4 block text-sm font-semibold">
            {t.password}
            <input type="password" className={inputClass} autoComplete="new-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
            <span className="mt-1 block text-xs font-normal text-muted-foreground">{t.passwordHint}</span>
            <FieldError msg={errors["password"]} />
          </label>
          <label className="mt-4 block text-sm font-semibold">
            {t.passwordConfirm}
            <input type="password" className={inputClass} autoComplete="new-password" value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} />
            <FieldError msg={errors["confirm"]} />
          </label>
          {/* Honeypot: hidden from people, filled by bots. */}
          <div aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 overflow-hidden">
            <label>
              Website
              <input tabIndex={-1} autoComplete="off" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} />
            </label>
          </div>
          <p className="mt-6 rounded-md bg-secondary p-4 text-xs leading-relaxed text-muted-foreground">{t.storedNotice}</p>
          <label className="mt-4 flex items-start gap-3 text-sm">
            <input type="checkbox" className="mt-1 size-4" checked={f.consent} onChange={(e) => setF({ ...f, consent: e.target.checked })} />
            <span>
              {t.consentPre}
              <a href={p.privacy} target="_blank" rel="noopener" className="font-bold text-primary underline">{t.consentLink}</a>
              {t.consentPost}
            </span>
          </label>
          <FieldError msg={errors["consent"]} />
          <button type="submit" disabled={busy} className={`mt-6 ${btnPrimary}`}>
            {busy ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : null}
            {t.registerBtn}
          </button>
        </form>
      )}
      <a href={p.login} className="mt-6 inline-block text-xs font-semibold text-primary underline">{t.toLogin}</a>
    </Shell>
  );
}

/* ---------------- Login ---------------- */

async function fetchOwnStatus(): Promise<ConsultantStatus | null | "none"> {
  const { data: u } = await supabase.auth.getUser();
  if (!u.user) return null;
  const { data } = await supabase.from("consultants").select("status").eq("user_id", u.user.id).maybeSingle();
  return (data?.status as ConsultantStatus | undefined) ?? "none";
}

export function LoginPage({ locale }: { locale: PortalLocale }) {
  const t = PORTAL_STRINGS[locale];
  const p = PORTAL_PATHS[locale];
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function go() {
    const status = await fetchOwnStatus();
    if (status === null) return;
    if (status === "none") {
      setError(t.noPortalAccount);
      return;
    }
    void navigate({ to: routeForStatus(status, locale), replace: true });
  }

  useEffect(() => {
    void go();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { error: err } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
    setBusy(false);
    if (err) {
      setError(t.errors["login"]!);
      return;
    }
    await go();
  }

  return (
    <Shell title={t.loginTitle} intro={t.loginIntro} locale={locale}>
      <form onSubmit={submit}>
        <label className="block text-sm font-semibold">
          {t.email}
          <input required type="email" autoComplete="email" className={inputClass} value={email} onChange={(e) => setEmail(e.target.value)} />
        </label>
        <label className="mt-4 block text-sm font-semibold">
          {t.password}
          <input required type="password" autoComplete="current-password" className={inputClass} value={password} onChange={(e) => setPassword(e.target.value)} />
        </label>
        {error ? <p className="mt-4 text-sm font-medium text-destructive" role="alert">{error}</p> : null}
        <button type="submit" disabled={busy} className={`mt-6 ${btnPrimary}`}>
          {busy ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : null}
          {t.loginBtn}
        </button>
      </form>
      <div className="mt-6 flex flex-wrap justify-between gap-3 text-xs font-semibold">
        <a href={p.forgot} className="text-primary underline">{t.forgotLink}</a>
        <a href={p.register} className="text-primary underline">{t.toRegister}</a>
      </div>
    </Shell>
  );
}

/* ---------------- Forgot / reset ---------------- */

export function ForgotPage({ locale }: { locale: PortalLocale }) {
  const t = PORTAL_STRINGS[locale];
  const p = PORTAL_PATHS[locale];
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    await supabase.auth.resetPasswordForEmail(email.trim(), { redirectTo: `${window.location.origin}${p.reset}` });
    setBusy(false);
    setSent(true);
  }

  return (
    <Shell title={t.forgotTitle} intro={t.forgotIntro} locale={locale}>
      {sent ? (
        <p className="text-sm text-muted-foreground" role="status">{t.resetSent}</p>
      ) : (
        <form onSubmit={submit}>
          <label className="block text-sm font-semibold">
            {t.email}
            <input required type="email" autoComplete="email" className={inputClass} value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
          <button type="submit" disabled={busy} className={`mt-6 ${btnPrimary}`}>
            {busy ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : null}
            {t.sendLink}
          </button>
        </form>
      )}
      <a href={p.login} className="mt-6 inline-block text-xs font-semibold text-primary underline">{t.toLogin}</a>
    </Shell>
  );
}

export function ResetPage({ locale }: { locale: PortalLocale }) {
  const t = PORTAL_STRINGS[locale];
  const p = PORTAL_PATHS[locale];
  const [ready, setReady] = useState(false);
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const { data } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY" || event === "SIGNED_IN") setReady(true);
    });
    void supabase.auth.getSession().then(({ data: s }) => s.session && setReady(true));
    return () => data.subscription.unsubscribe();
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const parsed = passwordSchema.safeParse(pw);
    if (!parsed.success) return setError(t.errors[parsed.error.issues[0]!.message] ?? t.errors["generic"]!);
    if (pw !== confirm) return setError(t.errors["password_mismatch"]!);
    setBusy(true);
    setError(null);
    const { error: err } = await supabase.auth.updateUser({ password: pw });
    setBusy(false);
    if (err) setError(t.errors["generic"]!);
    else {
      setDone(true);
      await supabase.auth.signOut();
    }
  }

  return (
    <Shell title={t.resetTitle} intro={t.resetIntro} locale={locale}>
      {done ? (
        <p className="text-sm text-muted-foreground" role="status">{t.resetDone}</p>
      ) : (
        <form onSubmit={submit}>
          {!ready ? <p className="mb-4 text-sm text-muted-foreground">{t.resetNeedLink}</p> : null}
          <label className="block text-sm font-semibold">
            {t.password}
            <input type="password" autoComplete="new-password" className={inputClass} value={pw} onChange={(e) => setPw(e.target.value)} />
            <span className="mt-1 block text-xs font-normal text-muted-foreground">{t.passwordHint}</span>
          </label>
          <label className="mt-4 block text-sm font-semibold">
            {t.passwordConfirm}
            <input type="password" autoComplete="new-password" className={inputClass} value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          </label>
          {error ? <p className="mt-4 text-sm font-medium text-destructive" role="alert">{error}</p> : null}
          <button type="submit" disabled={busy || !ready} className={`mt-6 ${btnPrimary}`}>
            {busy ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : null}
            {t.savePassword}
          </button>
        </form>
      )}
      <a href={p.login} className="mt-6 inline-block text-xs font-semibold text-primary underline">{t.toLogin}</a>
    </Shell>
  );
}

/* ---------------- Signed-in pages ---------------- */

type ConsultantRow = {
  first_name: string;
  last_name: string;
  street: string | null;
  postal_code: string | null;
  city: string | null;
  country: string | null;
  phone: string | null;
  email: string;
  birth_year: number | null;
  nationality: string | null;
  native_language: string | null;
  foreign_languages: unknown;
  social_profiles: unknown;
  availability_status: string | null;
  availability_note: string | null;
  earliest_start_date: string | null;
  consultant_role: string | null;
  status: ConsultantStatus;
};

/** Client-side session + row loader; RLS guarantees only the own row is readable. */
function useOwnConsultant(locale: PortalLocale) {
  const navigate = useNavigate();
  const [row, setRow] = useState<ConsultantRow | null | "none">(null);
  useEffect(() => {
    let alive = true;
    void (async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) {
        void navigate({ to: PORTAL_PATHS[locale].login, replace: true });
        return;
      }
      const { data } = await supabase.from("consultants").select("*").eq("user_id", u.user.id).maybeSingle();
      if (alive) setRow((data as ConsultantRow | null) ?? "none");
    })();
    return () => {
      alive = false;
    };
  }, [locale, navigate]);
  return row;
}

function SignOutButton({ locale }: { locale: PortalLocale }) {
  const t = PORTAL_STRINGS[locale];
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={async () => {
        await supabase.auth.signOut();
        void navigate({ to: PORTAL_PATHS[locale].login, replace: true });
      }}
      className="inline-flex items-center gap-2 rounded-md border border-border px-4 py-2 text-sm font-bold hover:bg-secondary"
    >
      <LogOut className="size-4" aria-hidden="true" /> {t.signOut}
    </button>
  );
}

function Loading() {
  return (
    <div className="flex justify-center py-10">
      <Loader2 className="size-6 animate-spin text-muted-foreground" aria-hidden="true" />
    </div>
  );
}

export function PendingPage({ locale }: { locale: PortalLocale }) {
  const t = PORTAL_STRINGS[locale];
  const navigate = useNavigate();
  const row = useOwnConsultant(locale);
  useEffect(() => {
    if (row && row !== "none" && row.status === "approved") void navigate({ to: PORTAL_PATHS[locale].profile, replace: true });
  }, [row, locale, navigate]);
  const body =
    row === "none" ? t.noPortalAccount : row?.status === "rejected" ? t.rejectedBody : row?.status === "blocked" ? t.blockedBody : t.pendingBody;
  return (
    <Shell title={t.pendingTitle} locale={locale}>
      {row === null ? <Loading /> : <p className="text-sm leading-relaxed text-muted-foreground" role="status">{body}</p>}
      <div className="mt-6"><SignOutButton locale={locale} /></div>
    </Shell>
  );
}

type FormState = {
  first_name: string;
  last_name: string;
  street: string;
  postal_code: string;
  city: string;
  country: string;
  phone: string;
  birth_year: string;
  nationality: string;
  native_language: string;
  foreign_languages: { language: string; level: string }[];
  social_profiles: Record<string, string>;
  availability_status: string;
  availability_note: string;
  earliest_start_date: string;
  consultant_role: string;
};

function toForm(r: ConsultantRow): FormState {
  const social = (r.social_profiles && typeof r.social_profiles === "object" ? r.social_profiles : {}) as Record<string, string>;
  return {
    first_name: r.first_name,
    last_name: r.last_name,
    street: r.street ?? "",
    postal_code: r.postal_code ?? "",
    city: r.city ?? "",
    country: r.country ?? "",
    phone: r.phone ?? "",
    birth_year: r.birth_year ? String(r.birth_year) : "",
    nationality: r.nationality ?? "",
    native_language: r.native_language ?? "",
    foreign_languages: Array.isArray(r.foreign_languages) ? (r.foreign_languages as { language: string; level: string }[]) : [],
    social_profiles: Object.fromEntries(SOCIAL_KEYS.map((k) => [k, social[k] ?? ""])),
    availability_status: r.availability_status ?? "",
    availability_note: r.availability_note ?? "",
    earliest_start_date: r.earliest_start_date ?? "",
    consultant_role: r.consultant_role ?? "",
  };
}

export function formToPayload(f: FormState) {
  return {
    ...f,
    birth_year: f.birth_year.trim() === "" ? null : Number(f.birth_year),
    availability_status: f.availability_status === "" ? null : f.availability_status,
  };
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <fieldset className="border-t border-border pt-6 first:border-t-0 first:pt-0">
      <legend className="font-display text-base font-bold">{title}</legend>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">{children}</div>
    </fieldset>
  );
}

export function ProfilePage({ locale }: { locale: PortalLocale }) {
  const t = PORTAL_STRINGS[locale];
  const navigate = useNavigate();
  const row = useOwnConsultant(locale);
  const save = useServerFn(saveConsultantProfile);
  const del = useServerFn(deleteOwnConsultantAccount);
  const [f, setF] = useState<FormState | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (row === "none" || (row && row.status !== "approved")) {
      void navigate({ to: PORTAL_PATHS[locale].pending, replace: true });
    } else if (row) setF(toForm(row));
  }, [row, locale, navigate]);

  const maxYear = useMemo(() => maxBirthYear(), []);

  if (!f || !row || row === "none") {
    return <Shell title={t.profileTitle} locale={locale} wide><Loading /></Shell>;
  }

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setF({ ...f, [k]: v });
  const text = (k: keyof FormState, label: string, props: React.InputHTMLAttributes<HTMLInputElement> = {}) => (
    <label className="block text-sm font-semibold">
      {label}
      <input className={inputClass} value={f[k] as unknown as string} onChange={(e) => set(k, e.target.value as never)} {...props} />
      <FieldError msg={errors[k]} />
    </label>
  );

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const parsed = profileSchema.safeParse(formToPayload(f!));
    if (!parsed.success) {
      setErrors(issuesToErrors(parsed.error.issues, t.errors));
      toast.error(t.saveFailed);
      return;
    }
    setErrors({});
    setBusy(true);
    try {
      await save({ data: formToPayload(f!) });
      toast.success(t.saved);
    } catch {
      toast.error(t.saveFailed);
    } finally {
      setBusy(false);
    }
  }

  async function doDelete() {
    setBusy(true);
    try {
      await del();
      await supabase.auth.signOut();
      toast.success(t.deleted);
      void navigate({ to: (locale === "tr" ? "/tr/uzmanlar-icin" : "/for-specialists"), replace: true });
    } catch {
      toast.error(t.errors["generic"]);
      setBusy(false);
    }
  }

  return (
    <Shell title={t.profileTitle} intro={t.profileIntro} locale={locale} wide>
      <div className="mb-6 flex justify-end"><SignOutButton locale={locale} /></div>
      <form onSubmit={submit} noValidate className="space-y-8">
        <Section title={t.sections.name}>
          {text("first_name", t.firstName, { autoComplete: "given-name" })}
          {text("last_name", t.lastName, { autoComplete: "family-name" })}
        </Section>
        <Section title={t.sections.address}>
          <div className="sm:col-span-2">{text("street", t.street, { autoComplete: "street-address" })}</div>
          {text("postal_code", t.postalCode, { autoComplete: "postal-code" })}
          {text("city", t.city, { autoComplete: "address-level2" })}
          {text("country", t.country, { maxLength: 2, autoComplete: "country" })}
        </Section>
        <Section title={t.sections.contact}>
          <label className="block text-sm font-semibold">
            {t.emailReadonly}
            <input className={inputClass} value={row.email} readOnly />
          </label>
          {text("phone", t.phone, { type: "tel", autoComplete: "tel" })}
        </Section>
        <Section title={t.sections.personal}>
          {text("birth_year", t.birthYear, { type: "number", min: MIN_BIRTH_YEAR, max: maxYear, inputMode: "numeric" })}
          {text("nationality", t.nationality, { maxLength: 2 })}
          {text("native_language", t.nativeLanguage)}
          <div className="sm:col-span-2">
            <p className="text-sm font-semibold">{t.foreignLanguages}</p>
            <ul className="mt-2 space-y-2">
              {f.foreign_languages.map((fl, i) => (
                <li key={i} className="flex flex-wrap items-end gap-2">
                  <input aria-label={t.language} placeholder={t.language} className={`${inputClass} mt-0 flex-1`} value={fl.language}
                    onChange={(e) => set("foreign_languages", f.foreign_languages.map((x, j) => (j === i ? { ...x, language: e.target.value } : x)))} />
                  <select aria-label={t.level} className={`${inputClass} mt-0 w-32`} value={fl.level}
                    onChange={(e) => set("foreign_languages", f.foreign_languages.map((x, j) => (j === i ? { ...x, level: e.target.value } : x)))}>
                    {LANGUAGE_LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
                  </select>
                  <button type="button" aria-label={t.remove} className="rounded-md border border-border p-3 hover:bg-secondary"
                    onClick={() => set("foreign_languages", f.foreign_languages.filter((_, j) => j !== i))}>
                    <Trash2 className="size-4" aria-hidden="true" />
                  </button>
                  <FieldError msg={errors[`foreign_languages.${i}.language`]} />
                </li>
              ))}
            </ul>
            <button type="button" className="mt-2 inline-flex items-center gap-2 text-sm font-bold text-primary"
              onClick={() => set("foreign_languages", [...f.foreign_languages, { language: "", level: "B2" }])}>
              <Plus className="size-4" aria-hidden="true" /> {t.addLanguage}
            </button>
          </div>
        </Section>
        <Section title={t.sections.social}>
          {SOCIAL_KEYS.map((k) => (
            <label key={k} className="block text-sm font-semibold capitalize">
              {k === "x" ? "X" : k}
              <input type="url" placeholder="https://" className={inputClass} value={f.social_profiles[k] ?? ""}
                onChange={(e) => set("social_profiles", { ...f.social_profiles, [k]: e.target.value })} />
              <FieldError msg={errors[`social_profiles.${k}`]} />
            </label>
          ))}
        </Section>
        <Section title={t.sections.availability}>
          <label className="block text-sm font-semibold">
            {t.availabilityStatus}
            <select className={inputClass} value={f.availability_status} onChange={(e) => set("availability_status", e.target.value)}>
              <option value="">{t.choose}</option>
              {AVAILABILITY.map((a) => <option key={a} value={a}>{t.availabilityOptions[a]}</option>)}
            </select>
          </label>
          {text("earliest_start_date", t.earliestStart, { type: "date" })}
          <div className="sm:col-span-2">
            {text("consultant_role", t.role, { list: "consultant-role-suggestions" })}
            <datalist id="consultant-role-suggestions">
              {ROLE_SUGGESTIONS.map((r) => <option key={r} value={r} />)}
            </datalist>
          </div>
          <label className="block text-sm font-semibold sm:col-span-2">
            {t.availabilityNote}
            <textarea rows={3} maxLength={500} className={inputClass} value={f.availability_note} onChange={(e) => set("availability_note", e.target.value)} />
          </label>
        </Section>
        <button type="submit" disabled={busy} className={btnPrimary}>
          {busy ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : null}
          {t.save}
        </button>
      </form>

      <div className="mt-12 rounded-md border border-destructive/40 p-5">
        <h2 className="font-display text-base font-bold text-destructive">{t.deleteTitle}</h2>
        <p className="mt-2 text-sm text-muted-foreground">{t.deleteBody}</p>
        {confirmDelete ? (
          <div className="mt-4 flex flex-wrap gap-3">
            <button type="button" disabled={busy} onClick={doDelete}
              className="rounded-md bg-destructive px-4 py-2 text-sm font-bold text-destructive-foreground disabled:opacity-60">
              {t.deleteConfirm}
            </button>
            <button type="button" onClick={() => setConfirmDelete(false)} className="rounded-md border border-border px-4 py-2 text-sm font-bold">
              {t.cancel}
            </button>
          </div>
        ) : (
          <button type="button" onClick={() => setConfirmDelete(true)}
            className="mt-4 rounded-md border border-destructive px-4 py-2 text-sm font-bold text-destructive hover:bg-destructive/10">
            {t.deleteTitle}
          </button>
        )}
      </div>
    </Shell>
  );
}

/* ---------------- CTA on the consultants page ---------------- */

export function PortalCta({ locale }: { locale: PortalLocale }) {
  const t = PORTAL_STRINGS[locale];
  const p = PORTAL_PATHS[locale];
  return (
    <section className="section-shell py-12" id="consultant-portal">
      <div className="flex flex-col gap-6 rounded-lg bg-ink p-8 text-ink-foreground shadow-elevate md:flex-row md:items-center md:justify-between md:p-10">
        <div className="max-w-2xl">
          <p className="font-display text-xs font-bold uppercase tracking-[0.2em] text-highlight">{t.ctaEyebrow}</p>
          <h2 className="mt-2 text-2xl font-bold md:text-3xl">{t.ctaTitle}</h2>
          <p className="mt-3 text-ink-muted">{t.ctaBody}</p>
        </div>
        <div className="flex shrink-0 flex-wrap gap-3">
          <Link to={p.register} className="rounded-md bg-highlight px-6 py-3 text-sm font-bold text-highlight-foreground">
            {t.ctaRegister}
          </Link>
          <Link to={p.login} className="rounded-md border border-ink-foreground/40 px-6 py-3 text-sm font-bold hover:bg-ink-foreground/10">
            {t.ctaLogin}
          </Link>
        </div>
      </div>
    </section>
  );
}
